/**
 * @voltxampere/ui — Symbol id alias map (Sprint 25 R2 lane 4a)
 *
 * Closes TS-001 from the Sprint 25 R1 triage. Mirror of the engine's
 * `SCHEMA_SYMBOL_ALIASES` (`packages/engine/src/stamps/register.ts`)
 * in the **reverse direction**: schema canonical type → UI symbol id.
 *
 * The engine alias map covers UI id → engine canonical so a circuit
 * authored against the UI symbol library (`type: "dc-source"`)
 * resolves to the engine's `voltage-source` stamp. The UI side had
 * no inverse map, so circuits authored against the schema canonical
 * (`type: "voltage-source"` — every preset in `packages/presets/`)
 * silently failed `findSymbolById` and emitted
 * `[VXA canvas] No symbol registered for component type "..."` to
 * the console without rendering anything.
 *
 * This map closes that gap. `findSymbolById` consults it as a
 * fallback after the direct UI-id lookup, so:
 *
 *   - Boot fixture (`canvas-demo.ts`) — uses UI ids → direct match
 *   - Place command (`armedSymbol = sym`) — uses UI ids → direct match
 *   - Preset library (`@voltxampere/presets`) — uses schema canonical
 *     → alias resolves to UI id
 *   - Schema-faithful test fixtures (`worker-stub.ts`,
 *     `transient-demo.ts`) — schema canonical → alias resolves
 *
 * Coverage matches the 7 Class A mismatches identified in
 * `tools/triage/reports/TRIAGE_LANE4_FINDINGS.md` §1 (the
 * 5-dimension type matrix).
 *
 * **Polarity carve-out (jfet-n / jfet-p):**
 * Sprint 23 split JFET into two engine stamps (`jfet-n` and
 * `jfet-p` with the Shichman-Hodges square-law in different
 * polarities). The UI library still ships a single `jfet` symbol
 * because the visual schematic glyph is identical — polarity is
 * conveyed by labels and the channel arrow direction. Both
 * canonical types map to the single UI symbol; the polarity
 * survives in `instance.params` (and the engine reads it from
 * `instance.type`). Future R3 lane may split the UI symbol if
 * users request distinct head/arrow rendering.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Schema canonical component type → UI symbol id.
 *
 * Order mirrors the engine alias map for ease of cross-reference;
 * additions here MUST be paired with engine alias updates so the
 * round trip (schema → UI → engine) stays loss-free.
 */
export const SCHEMA_TO_UI_SYMBOL = Object.freeze({
    "voltage-source": "dc-source",
    "bjt-npn": "npn",
    "bjt-pnp": "pnp",
    "mosfet-nmos": "nmos",
    "mosfet-pmos": "pmos",
    opamp: "op-amp",
    "wire-node": "node",
    // JFET polarity-collapse — both schema canonicals share the
    // single UI symbol id `jfet`.
    "jfet-n": "jfet",
    "jfet-p": "jfet",
});
/**
 * Resolve a component instance type string to its UI symbol id.
 * Returns the input unchanged when no alias is registered, so a
 * caller that already holds a UI id passes through cleanly.
 */
export function canonicalToUiSymbolId(canonical) {
    return SCHEMA_TO_UI_SYMBOL[canonical] ?? canonical;
}
//# sourceMappingURL=aliases.js.map